import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Bell,
  X,
  List,
  Loader2,
  MailX,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';
import {
  type GroupedAlert,
  OrganizationScanAlertsSortField,
} from '@kirin/api-client/contracts/alerts';
import { ApiError } from '@kirin/api-client/errors';
import { SortOrder } from '@kirin/api-client/contracts/helpers';
import {
  useOrganizationAlertRef,
  useAccountAlertRef,
} from '@/hooks/api/useGroupedAlerts';
import { useCanViewAdminContent } from '@/hooks/useRoleCheck';
import type { AddFromAlertScope } from '@/hooks/api/useAddPolicyExceptionFromAlert';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { AlertMuteButton } from '@/components/AlertMuteButton';
import { AlertUnmuteButton } from '@/components/AlertUnmuteButton';
import { AlertExcludeButton } from '@/components/AlertExcludeButton';
import { AlertRemoveExclusionButton } from '@/components/AlertRemoveExclusionButton';
import { AlertEditPolicyButton } from '@/components/AlertEditPolicyButton';
import {
  AlertsTable,
  type AlertsTableColumn,
  FILE_PATH_FIELD,
} from './AlertsTable';
import { AlertOccurrencesModal } from './AlertOccurrencesModal';
import {
  alertsListFallbackPath,
  accountAlertsListFallbackPath,
  hasDeepLinkFilterParams,
} from './deep-link-fallback';

/**
 * Which alerts surface the notification landed on — an organization
 * (`/{org}/alerts/{token}`) or a personal account (`/dashboard/alerts/{token}`).
 * Drives which resolve endpoint, base path, and fallback list are used, so a
 * no-org user gets the same scoped experience as an org user.
 */
export type ScopedAlertsScope =
  | { kind: 'org'; organizationId: string }
  | { kind: 'account' };

export interface ScopedAlertsViewProps {
  scope: ScopedAlertsScope;
  /** The opaque 64-hex notification token — a finding_key or an alert-set id. */
  token: string;
}

// KIR-2788: only the columns that are ACCURATE for this specific notification —
// the finding's intrinsic properties (type, severity, outcome, file, score) plus
// each row's own "Last triggered" = its most-recent detection (lastSeenAt), the
// per-alert analogue of the header's single-alert time. The remaining
// grouped-alert lifetime aggregates (occurrence count, the users/endpoints it
// ever spanned) describe the alert's whole history, not the one event the
// notification fired for, so they are omitted here.
// Display-only (sortable: false): the set is small and fixed.
const SCOPED_COLUMNS: AlertsTableColumn<OrganizationScanAlertsSortField>[] = [
  {
    field: OrganizationScanAlertsSortField.Type,
    label: 'Type',
    sortable: false,
  },
  {
    field: OrganizationScanAlertsSortField.Severity,
    label: 'Severity',
    sortable: false,
  },
  {
    field: OrganizationScanAlertsSortField.Outcome,
    label: 'Outcome',
    sortable: false,
  },
  { field: FILE_PATH_FIELD, label: 'File', sortable: false },
  {
    field: OrganizationScanAlertsSortField.Score,
    label: 'Score',
    sortable: false,
  },
  {
    field: OrganizationScanAlertsSortField.LastSeenAt,
    label: 'Last triggered',
    sortable: false,
  },
];

const SEVERITY_ORDER = ['critical', 'high', 'medium', 'low', 'info'];

/**
 * KIR-2788: derive an accurate one-line summary of the grouped set from the
 * resolved alerts themselves — count + severity spread (e.g. "2 Critical, 1
 * High"). It describes *why these are together* the way the old filter chips
 * did, but without asserting a filter the selection didn't use: the set is the
 * exact finding_keys the notification fired for.
 */
function buildSeveritySummary(alerts: GroupedAlert[]): string {
  const counts = new Map<string, number>();
  for (const alert of alerts) {
    const key = (alert.severity ?? 'unknown').toLowerCase();
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  // Known severities first, in rank order; then any leftover bucket (e.g.
  // 'unknown' from a null/unrecognized severity) so the spread always sums to
  // the header's "N alerts" total instead of silently dropping members.
  const known = SEVERITY_ORDER.filter(sev => counts.has(sev));
  const extras = [...counts.keys()].filter(
    sev => !SEVERITY_ORDER.includes(sev)
  );
  return [...known, ...extras]
    .map(
      sev => `${counts.get(sev)} ${sev.charAt(0).toUpperCase()}${sev.slice(1)}`
    )
    .join(', ');
}

/**
 * KIR-4664: how long the expired-alert splash lingers before auto-redirecting
 * to the recent-alerts list. Long enough to read the message and reach the
 * button, short enough not to strand the user. A whole number of seconds so the
 * on-screen countdown stays exact.
 */
const EXPIRED_SPLASH_REDIRECT_SECONDS = 5;
const EXPIRED_SPLASH_REDIRECT_MS = EXPIRED_SPLASH_REDIRECT_SECONDS * 1000;

/**
 * KIR-4664: shown when a notification token resolves to nothing (an alert
 * deleted after retention, or an unknown/expired token) AND the deep link
 * carries no useful filter params to fall back to. Replaces the previous silent
 * `<Navigate>` to a bare list with a clear, accessible empty state: an icon, a
 * plain message with a live countdown, an explicit "View recent alerts" button,
 * and a short auto-redirect so a user who does nothing still lands somewhere
 * useful.
 */
function ExpiredAlertSplash({
  recentAlertsPath,
}: {
  recentAlertsPath: string;
}): ReactElement {
  const navigate = useNavigate();
  const [secondsLeft, setSecondsLeft] = useState(
    EXPIRED_SPLASH_REDIRECT_SECONDS
  );
  useEffect(() => {
    const redirect = setTimeout(
      () => navigate(recentAlertsPath, { replace: true }),
      EXPIRED_SPLASH_REDIRECT_MS
    );
    // Tick the visible countdown once per second, floored at 1 so it never
    // shows 0 before the redirect fires.
    const tick = setInterval(
      () => setSecondsLeft(s => (s > 1 ? s - 1 : 1)),
      1000
    );
    return () => {
      clearTimeout(redirect);
      clearInterval(tick);
    };
  }, [navigate, recentAlertsPath]);

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex flex-col items-center justify-center gap-3 p-12 text-center"
    >
      <MailX className="h-10 w-10 text-muted-foreground" aria-hidden="true" />
      <p className="max-w-md text-sm text-muted-foreground">
        This notification&rsquo;s alert has expired or is no longer available.
        Redirecting to your recent alerts in {secondsLeft}s…
      </p>
      <Button asChild variant="outline" size="sm">
        <Link to={recentAlertsPath}>
          <List className="mr-2 h-4 w-4" />
          View recent alerts
        </Link>
      </Button>
    </div>
  );
}

/**
 * KIR-4664 (P3): shown when the token *fetch itself* failed (a 500, a timeout, a
 * dropped connection) — as opposed to a token that resolved to an empty set. A
 * transient infrastructure error must NOT be reported as "expired" (a false claim
 * about the user's data) and must NOT auto-redirect away, or the user can't retry
 * without navigating back to the notification. Offers an explicit Retry (re-runs
 * the same ref query) plus a manual escape hatch to recent alerts.
 */
function ScopedAlertsLoadError({
  onRetry,
  recentAlertsPath,
}: {
  onRetry: () => void;
  recentAlertsPath: string;
}): ReactElement {
  return (
    <div
      role="alert"
      className="flex flex-col items-center justify-center gap-3 p-12 text-center"
    >
      <AlertTriangle
        className="h-10 w-10 text-muted-foreground"
        aria-hidden="true"
      />
      <p className="max-w-md text-sm text-muted-foreground">
        Couldn&rsquo;t load this alert. This is usually temporary &mdash; please
        try again.
      </p>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={onRetry}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Retry
        </Button>
        <Button asChild variant="ghost" size="sm">
          <Link to={recentAlertsPath}>
            <List className="mr-2 h-4 w-4" />
            View recent alerts
          </Link>
        </Button>
      </div>
    </div>
  );
}

/**
 * KIR-2788: the unified notification landing rendered as the *real* alerts
 * table, scoped to exactly the alert(s) the token resolves to (Option B). Every
 * column, cell, and row-expansion is the shared {@link AlertsTable}, so an
 * analyst gets the full list experience — never a heuristic filter. A scope chip
 * marks the view as grouped-from-a-notification (with when it fired) and clears
 * to the full list; an unknown / expired / wrong-scope token (or empty set)
 * degrades to the alerts list carrying the deep link's filter query.
 */
export function ScopedAlertsView({
  scope,
  token,
}: ScopedAlertsViewProps): ReactElement {
  const [searchParams] = useSearchParams();
  // KIR-2788: open occurrences in place (a modal over this scoped view) so
  // closing it returns here, not to the full alerts list.
  const [occurrencesKey, setOccurrencesKey] = useState<string | null>(null);
  // Resolve against the right scope. Both hooks are called (rules of hooks); the
  // `enabled` flag ensures only the active one fetches.
  const isOrg = scope.kind === 'org';
  const organizationId = isOrg ? scope.organizationId : undefined;
  const orgQuery = useOrganizationAlertRef(organizationId, token, isOrg);
  const accountQuery = useAccountAlertRef(token, !isOrg);
  const { data, isLoading, isError, error, refetch } = isOrg
    ? orgQuery
    : accountQuery;

  const alerts = useMemo(() => {
    const resolved = data?.alerts ?? [];
    // KIR-4664: default the notification landing to "last triggered" order —
    // each row's own most-recent detection (`lastSeenAt`) DESCENDING, so the
    // freshest event the notification fired for is on top. Severity is conveyed
    // by the header summary spread, not the row order. ISO-8601 timestamps sort
    // lexicographically = chronologically; a missing time collates as '' (last).
    return [...resolved].sort((a, b) =>
      (b.lastSeenAt ?? '').localeCompare(a.lastSeenAt ?? '')
    );
  }, [data]);

  // KIR-4664: the resolved-alert scope threaded into every reused action button
  // (Mute/Exclude/Edit Policy/Comment) and the expanded card. The four
  // suppression scopes, `PolicyEditScope`, and `AddFromAlertScope` are all the
  // same `{ organization | account }` shape, so one object serves them all.
  const actionScope = useMemo<AddFromAlertScope>(
    () =>
      scope.kind === 'org'
        ? { kind: 'organization', organizationId: scope.organizationId }
        : { kind: 'account' },
    [scope]
  );

  // The ref query is a *separate* cache entry from the grouped-alerts lists the
  // suppression/feedback hooks invalidate, so a mutation there never refreshes
  // this view on its own — refetch it explicitly on each action's success so the
  // row reflects the new muted/excluded/comment state in place (KIR-4664).
  const refetchRef = useCallback((): void => {
    void refetch();
  }, [refetch]);

  // Org-only surfaces (the hook tolerates an undefined org id → no access).
  const { canViewAdminContent, isLoading: canViewLoading } =
    useCanViewAdminContent(organizationId);

  // Org: the viewer can see org-wide content (owner/manager/support). Account:
  // no role gate. Each button also self-gates on role, so this only decides
  // whether the Actions column appears at all (and thus whether a header /
  // empty cells would show).
  const showActions = isOrg ? !canViewLoading && canViewAdminContent : true;

  // Reuses the exact button set from `createOrganizationAlertsActionsColumn` /
  // `createUserAlertsActionsColumn`, plus the org Comment control — all wired to
  // the SAME mutation hooks (never re-implemented) and refetching the ref query
  // on success. `AlertEditPolicyButton` owns its own dialog/invalidation and has
  // no row-level success callback, matching the main tabs.
  const renderRowActions = useCallback(
    (alert: GroupedAlert): ReactNode => {
      const analyticsContext = { severity: alert.severity, type: alert.type };
      return (
        <>
          {alert.mute?.muted ? (
            <AlertUnmuteButton
              scope={actionScope}
              alertKey={alert.alertKey}
              mute={alert.mute}
              analyticsContext={analyticsContext}
              onUnmuted={refetchRef}
              iconOnly
            />
          ) : (
            <AlertMuteButton
              scope={actionScope}
              alertKey={alert.alertKey}
              analyticsContext={analyticsContext}
              onMuted={refetchRef}
              iconOnly
            />
          )}
          {alert.exclude?.excluded ? (
            <AlertRemoveExclusionButton
              scope={actionScope}
              alertKey={alert.alertKey}
              exclude={alert.exclude}
              analyticsContext={analyticsContext}
              onRemoved={refetchRef}
              iconOnly
            />
          ) : (
            <AlertExcludeButton
              scope={actionScope}
              alertKey={alert.alertKey}
              analyticsContext={analyticsContext}
              findingType={alert.type}
              onExcluded={refetchRef}
              iconOnly
            />
          )}
          <AlertEditPolicyButton
            scope={actionScope}
            policyKey={alert.policyKey}
            subPolicyKey={alert.subPolicyKey}
            alertKey={alert.alertKey}
            iconOnly
          />
        </>
      );
    },
    [actionScope, refetchRef]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const alertsBasePath = isOrg ? `/${organizationId}` : '/dashboard';
  const allAlertsPath = `${alertsBasePath}/alerts`;

  // KIR-4664 (P3): a 404 from resolveAlertRef means the TOKEN is unresolvable —
  // unknown, expired past retention, or wrong-scope (the alerts route 404s). That
  // is not a transient outage; it degrades to the filtered list / expired splash
  // below, exactly like a resolved-empty set. `retryHandlers.detail` already fails
  // a 404 fast (no retry), so reaching here with one is terminal.
  const isTokenUnresolvable = error instanceof ApiError && error.status === 404;

  if (isError && !isTokenUnresolvable) {
    // A genuine transient failure (5xx / timeout / dropped connection / network),
    // NOT a purged token. Offer Retry instead of falsely claiming the alert
    // "expired" and redirecting the user away.
    return (
      <ScopedAlertsLoadError
        onRetry={refetchRef}
        recentAlertsPath={allAlertsPath}
      />
    );
  }

  if (isTokenUnresolvable || alerts.length === 0) {
    // KIR-4664: the token resolved to nothing — a 404 (purged / expired / unknown
    // / wrong-scope) or an empty set. Two distinct outcomes, decided by whether
    // the deep link still carries the KIR-4320 fallback filter query:
    //   - filter params present → degrade to the FILTERED list built from them
    //     (unchanged behavior), so the user still sees the relevant alerts —
    //     including window-end links that carry `t[includes]`.
    //   - no useful filter params (older agent / bare token) → show the
    //     expired-alert splash instead of silently landing on a bare list.
    if (hasDeepLinkFilterParams(searchParams)) {
      const fallbackPath = isOrg
        ? alertsListFallbackPath(organizationId ?? '', searchParams)
        : accountAlertsListFallbackPath(searchParams);
      return <Navigate to={fallbackPath} replace />;
    }
    return <ExpiredAlertSplash recentAlertsPath={allAlertsPath} />;
  }
  const summary = buildSeveritySummary(alerts);
  const triggeredAt = data?.triggeredAt ?? null;
  const triggeredBy = data?.triggeredBy ?? null;
  const triggeredByLabel = triggeredBy?.name ?? triggeredBy?.email ?? null;

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-muted-foreground">
        <Badge
          data-testid="notification-scope-chip"
          variant="secondary"
          className="gap-1.5 py-1 pl-2 pr-1 font-normal"
        >
          <Bell className="h-3.5 w-3.5" />
          {alerts.length === 1
            ? 'From a notification'
            : 'Grouped from one notification'}
          <Link
            to={allAlertsPath}
            aria-label="Clear grouping and show all alerts"
            className="ml-0.5 inline-flex rounded-sm p-0.5 hover:bg-foreground/10"
          >
            <X className="h-3.5 w-3.5" />
          </Link>
        </Badge>
        <span>
          {alerts.length} alert{alerts.length !== 1 ? 's' : ''}
          {summary ? ` · ${summary}` : ''}
        </span>
        {triggeredAt && (
          <span className="inline-flex items-center gap-1">
            {/* KIR-2788: a composite set's time is the exact moment the summary
                notification fired (its created_at) → "Triggered"; a single alert
                has no persisted set, so the time is its most-recent detection
                (lastSeenAt), which can advance on re-detection → "Last
                triggered". Same single-vs-grouped split as the chip above. */}
            · {alerts.length === 1 ? 'Last triggered' : 'Triggered'}{' '}
            <RelativeTimeWithTooltip date={triggeredAt} />
          </span>
        )}
        {/* KIR-2788 Tier 1: who fired the notification — shown to an elevated
            viewer opening a teammate's link (only the alert-set path returns
            triggeredBy; a single finding_key surfaces its user in the row). */}
        {triggeredByLabel && (
          <span className="inline-flex items-center gap-1">
            · Triggered by{' '}
            <span className="font-medium text-foreground">
              {triggeredByLabel}
            </span>
            {triggeredBy?.name && triggeredBy?.email && (
              <span>· {triggeredBy.email}</span>
            )}
          </span>
        )}
      </div>

      <Card>
        <CardContent className="pt-6">
          <AlertsTable
            alerts={alerts}
            pagination={{ total: alerts.length, hasMore: false }}
            page={1}
            onPrevious={() => {}}
            onNext={() => {}}
            sortBy={OrganizationScanAlertsSortField.LastSeenAt}
            sortOrder={SortOrder.Desc}
            onSort={() => {}}
            columns={SCOPED_COLUMNS}
            alertsBasePath={alertsBasePath}
            onShowOccurrences={setOccurrencesKey}
            detailScope={actionScope}
            {...(organizationId
              ? { detailOrganizationId: organizationId }
              : {})}
            {...(showActions ? { renderRowActions } : {})}
          />
        </CardContent>
      </Card>

      {occurrencesKey && (
        <AlertOccurrencesModal
          open
          alertKey={occurrencesKey}
          organizationId={organizationId}
          onClose={() => setOccurrencesKey(null)}
        />
      )}

      <div>
        <Link
          to={allAlertsPath}
          className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
        >
          <List className="h-4 w-4" />
          View all alerts
        </Link>
      </div>
    </div>
  );
}
